import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@store/index";
import { MultiselectOption } from "@shared/components";
import CommonUtils from "@shared/utils/CommonUtils";
import { entitledAccountsSelector, entitledFundsSelector } from "@store/Entities/selectors";
import { accountFundAccessSelector } from "@myProfile/store/selectors";
import { RequestContactUpdateState } from "../models";

export const requestContactUpdateRootSelector = (state: RootState): RequestContactUpdateState =>
  state.requestContactUpdate;

const selectedAccountsSelector = (state: RootState): RequestContactUpdateState["commonFields"]["selectedAccounts"] =>
  state.requestContactUpdate.commonFields.selectedAccounts;

const selectedFundsSelector = (state: RootState): RequestContactUpdateState["commonFields"]["selectedFunds"] =>
  state.requestContactUpdate.commonFields.selectedFunds;

// Build fund-to-accounts mapping
// Priority: 1. accountFundAccessData (My Profile API - most reliable)
//           2. entitledFunds.entitledAccountIds (like Documents page)
//           3. entitledAccount.fundGlobalId (fallback)
const fundToAccountsMapSelector = createSelector(
  accountFundAccessSelector,
  entitledFundsSelector,
  entitledAccountsSelector,
  (accountFundAccessSlice, fundsSlice, accountsSlice) => {
    const mapping: Record<string, Set<string>> = {};

    // PRIMARY: Use accountFundAccessData (most reliable - from My Profile API)
    if (accountFundAccessSlice.data?.accounts) {
      accountFundAccessSlice.data.accounts.forEach((account) => {
        if (account.accountGlobalId) {
          account.funds.forEach((fund) => {
            if (fund.fundGlobalId) {
              if (!mapping[fund.fundGlobalId]) {
                mapping[fund.fundGlobalId] = new Set();
              }
              mapping[fund.fundGlobalId].add(account.accountGlobalId as string);
            }
          });
        }
      });
    }

    // Secondary: Use entitledFunds.entitledAccountIds (same as Documents page)
    if (fundsSlice.data) {
      Object.entries(fundsSlice.data).forEach(([fundGlobalId, fund]) => {
        if (fund.entitledAccountIds && fund.entitledAccountIds.length > 0) {
          if (!mapping[fundGlobalId]) {
            mapping[fundGlobalId] = new Set();
          }
          fund.entitledAccountIds.forEach((accountId) => {
            mapping[fundGlobalId].add(accountId);
          });
        }
      });
    }

    // Fallback: Use entitledAccount.fundGlobalId for accounts not covered above
    if (accountsSlice.data) {
      Object.values(accountsSlice.data).forEach((account) => {
        if (account.fundGlobalId) {
          if (!mapping[account.fundGlobalId]) {
            mapping[account.fundGlobalId] = new Set();
          }
          mapping[account.fundGlobalId].add(account.globalId);
        }
      });
    }

    return mapping;
  }
);

// Build account-to-funds mapping (reverse mapping for account→fund filtering)
// Priority: 1. accountFundAccessData (My Profile API - most reliable)
//           2. entitledFunds.entitledAccountIds (like Documents page)
//           3. entitledAccount.fundGlobalId (fallback)
const accountToFundsMapSelector = createSelector(
  accountFundAccessSelector,
  entitledFundsSelector,
  entitledAccountsSelector,
  (accountFundAccessSlice, fundsSlice, accountsSlice) => {
    const mapping: Record<string, Set<string>> = {};

    // PRIMARY: Use accountFundAccessData (most reliable - from My Profile API)
    if (accountFundAccessSlice.data?.accounts) {
      accountFundAccessSlice.data.accounts.forEach((account) => {
        if (account.accountGlobalId) {
          account.funds.forEach((fund) => {
            if (fund.fundGlobalId) {
              if (!mapping[account.accountGlobalId as string]) {
                mapping[account.accountGlobalId as string] = new Set();
              }
              mapping[account.accountGlobalId as string].add(fund.fundGlobalId);
            }
          });
        }
      });
    }

    // Secondary: Build reverse mapping from entitledFunds.entitledAccountIds
    if (fundsSlice.data) {
      Object.entries(fundsSlice.data).forEach(([fundGlobalId, fund]) => {
        if (fund.entitledAccountIds && fund.entitledAccountIds.length > 0) {
          fund.entitledAccountIds.forEach((accountId) => {
            if (!mapping[accountId]) {
              mapping[accountId] = new Set();
            }
            mapping[accountId].add(fundGlobalId);
          });
        }
      });
    }

    // Fallback: Use entitledAccount.fundGlobalId
    if (accountsSlice.data) {
      Object.values(accountsSlice.data).forEach((account) => {
        if (account.fundGlobalId) {
          if (!mapping[account.globalId]) {
            mapping[account.globalId] = new Set();
          }
          mapping[account.globalId].add(account.fundGlobalId);
        }
      });
    }

    return mapping;
  }
);

// Extract grid-visible accounts from accountFundAccess API
const gridVisibleAccountsSelector = createSelector(accountFundAccessSelector, (accountFundAccessSlice) => {
  if (!accountFundAccessSlice.data?.accounts) return [];

  const accountsMap = new Map<string, { accountGlobalId: string; accountName: string }>();

  accountFundAccessSlice.data.accounts.forEach((account) => {
    if (account.accountGlobalId && account.accountName) {
      accountsMap.set(account.accountGlobalId, {
        accountGlobalId: account.accountGlobalId,
        accountName: account.accountName,
      });
    }
  });

  return Array.from(accountsMap.values());
});

// Cascaded accounts selector - filters grid-visible accounts based on selected funds
const accountsSelector = createSelector(
  gridVisibleAccountsSelector,
  selectedAccountsSelector,
  selectedFundsSelector,
  fundToAccountsMapSelector,
  (gridVisibleAccounts, selectedAccounts, selectedFunds, fundToAccountsMap) => {
    const selectedAccountsMap = CommonUtils.convertArrayToMap(selectedAccounts);

    if (gridVisibleAccounts.length === 0) return [];

    let filteredAccounts = [...gridVisibleAccounts];

    // If funds are selected, filter accounts to only those related to selected funds (from grid)
    if (selectedFunds.length > 0) {
      const allowedAccountIds = new Set<string>();

      selectedFunds.forEach((fundId) => {
        const accountIds = fundToAccountsMap[fundId];

        if (accountIds) {
          accountIds.forEach((accountId) => allowedAccountIds.add(accountId));
        }
      });

      filteredAccounts = filteredAccounts.filter((account) => allowedAccountIds.has(account.accountGlobalId));
    }

    const accounts: MultiselectOption[] = filteredAccounts.map((account) => ({
      id: account.accountGlobalId,
      label: account.accountName,
      selected: selectedAccountsMap[account.accountGlobalId],
    }));

    // Sort alphabetically in ascending order
    return accounts.sort((a, b) => {
      const labelA = a.label || "";
      const labelB = b.label || "";

      return labelA.localeCompare(labelB);
    });
  }
);

// Extract grid-visible funds from accountFundAccess API
const gridVisibleFundsSelector = createSelector(accountFundAccessSelector, (accountFundAccessSlice) => {
  if (!accountFundAccessSlice.data?.accounts) return [];

  const fundsMap = new Map<string, { fundGlobalId: string; fundName: string }>();

  accountFundAccessSlice.data.accounts.forEach((account) => {
    account.funds.forEach((fund) => {
      if (fund.fundGlobalId && fund.fundName) {
        fundsMap.set(fund.fundGlobalId, {
          fundGlobalId: fund.fundGlobalId,
          fundName: fund.fundName,
        });
      }
    });
  });

  return Array.from(fundsMap.values());
});

// Cascaded funds selector - filters grid-visible funds based on selected accounts
const fundsSelector = createSelector(
  gridVisibleFundsSelector,
  selectedFundsSelector,
  selectedAccountsSelector,
  accountToFundsMapSelector,
  (gridVisibleFunds, selectedFunds, selectedAccounts, accountToFundsMap) => {
    const selectedFundsMap = CommonUtils.convertArrayToMap(selectedFunds);

    if (gridVisibleFunds.length === 0) return [];

    let filteredFunds = [...gridVisibleFunds];

    // If accounts are selected, filter funds to only those related to selected accounts (from grid)
    if (selectedAccounts.length > 0) {
      const allowedFundIds = new Set<string>();

      selectedAccounts.forEach((accountId) => {
        const fundIds = accountToFundsMap[accountId];

        if (fundIds) {
          fundIds.forEach((fundId) => allowedFundIds.add(fundId));
        }
      });

      filteredFunds = filteredFunds.filter((fund) => allowedFundIds.has(fund.fundGlobalId));
    }

    const funds: MultiselectOption[] = filteredFunds.map((fund) => ({
      id: fund.fundGlobalId,
      label: fund.fundName,
      selected: selectedFundsMap[fund.fundGlobalId],
    }));

    // Sort alphabetically by ascending order (same as navigation menu)
    return funds.sort((a, b) => {
      const labelA = a.label.toLowerCase();
      const labelB = b.label.toLowerCase();

      return labelA > labelB ? 1 : -1;
    });
  }
);

export const requestContactUpdateSelector = createSelector(
  requestContactUpdateRootSelector,
  accountFundAccessSelector,
  (investorRequest, accountFundAccessSlice) => ({
    ...investorRequest,
    loading: investorRequest.loading || accountFundAccessSlice.loading,
  })
);

export const requestContactUpdatePreparedDataSelector = createSelector(
  requestContactUpdateRootSelector,
  accountsSelector,
  fundsSelector,
  ({ commonFields, customFields }, accounts, funds) => ({
    customFields,
    accounts,
    funds,
    message: commonFields.message,
    contactName: commonFields.contactName,
    contactEmail: commonFields.contactEmail,
  })
);
